import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IonicModule, ModalController } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { Chart, LinearScale } from 'chart.js';
import { SankeyController, Flow } from 'chartjs-chart-sankey';
import Markdown from 'markdown-it';
import moment from 'moment-timezone';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { DatetimeModalComponent } from 'src/app/components/datetime-modal/datetime-modal.component';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { CONVERSATION_STATUS, DATE_OPTIONS, DATE_RANGE_TYPES } from 'src/app/constants';
import { FilterOption, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { downloadCSV, getHtmlFromSlackMd, getWhereClausesFromFilters } from 'src/app/utils/utils';
import { FetchDataRequest, FetchDataResponse, createDataSource } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { AiResponse, WhereClause } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

interface DeflectedStatusCounts {
  stale?: number;
  closedBySubmitter?: number;
  closedByInternalUser?: number;
  closedByExternalUser?: number;
}

const SANKEY_LABELS = {
  TOTAL_CONVERSATIONS: 'Total conversations',
  NON_DEFLECTED_CONVERSATIONS: 'Non-deflected conversations',
  DEFLECTED_CONVERSATIONS: 'Deflected conversations',
  STALE_INTAKE: 'Stale intake',
  CLOSED_BY_SUBMITTER: 'Closed by submitter',
  CLOSED_BY_INTERNAL_USER: 'Closed by internal user',
  CLOSED_BY_EXTERNAL_USER: 'Closed by external user',
};

// Original color mapping using base labels
const colors = {
  [SANKEY_LABELS.TOTAL_CONVERSATIONS]: 'rgb(255, 177, 66)',
  [SANKEY_LABELS.NON_DEFLECTED_CONVERSATIONS]: 'rgb(255, 82, 82)',
  [SANKEY_LABELS.DEFLECTED_CONVERSATIONS]: '#2fdf75',
  [SANKEY_LABELS.STALE_INTAKE]: '#333333',
  [SANKEY_LABELS.CLOSED_BY_SUBMITTER]: '#2fdf75',
  [SANKEY_LABELS.CLOSED_BY_INTERNAL_USER]: '#a97bd9',
  [SANKEY_LABELS.CLOSED_BY_EXTERNAL_USER]: '#0080ff',
};

interface ConversationCountCall {
  baseStatuses: {
    open: number;
    inProgress: number;
    closed: number;
    draft: number;
    onHold: number;
  };
  deflectedStatuses: DeflectedStatusCounts;
}

Chart.register(SankeyController, Flow, LinearScale);

@Component({
  standalone: true,
  selector: 'app-ai-analytics',
  templateUrl: './ai-analytics.component.html',
  styleUrls: ['./ai-analytics.component.scss'],
  imports: [
    CommonModule,
    SharedDirectivesModule,
    FormsModule,
    IonicModule,
    RouterModule,
    NgxTippyModule,
    NgxDatatableModule,
    FilterButtonComponent,
  ],
})
export class AiAnalyticsComponent implements OnInit, OnDestroy {
  @ViewChild('myChart') myChartCanvas: ElementRef<HTMLCanvasElement>;
  chart: Chart;
  private themeObserver: MutationObserver;

  filterType: 'all' | 'thumbs_up' | 'thumbs_down' | 'no_response' | 'has_response' = 'all';
  feedbackItems = [];
  isInitialized = false;
  isLoading: boolean;
  allAiResponses: number;
  deflectedAiResponses: number;
  deflectedAiResponseCounts: DeflectedStatusCounts;
  dateRange = DATE_OPTIONS[1];
  draft = 0;
  readonly draftStatus = CONVERSATION_STATUS.INTAKE;
  inProgress = 0;
  open = 0;
  closed = 0;
  isDateRangeInvalid: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timezone: string;
  limit = 50;
  cursor: any;
  sortField = 'createdAt';
  sortDesc = true;

  filterOptions: FilterOption[] = [
    {
      label: 'Ticket Type',
      id: 'ticketTypeId',
      type: 'select',
    },
    {
      label: 'Project',
      id: 'projectId',
      type: 'select',
    },
  ];
  filters: SelectedFilterMap = new Map();

  constructor(
    private apiService: ApiService,
    private analyticsService: AnalyticsService,
    private modalCtrl: ModalController,
    private searchablePopoverService: SearchablePopoverService,
    public authService: AuthService,
  ) {}

  async ngOnInit() {
    this.isInitialized = false;

    await this.loadResponses();

    this.timezone = moment.tz.guess();
    this.endDate = moment().toDate();
    this.startDate = moment(this.endDate).startOf('day').subtract(7, 'days').toDate();

    await this.getConversationResponses();
    this.loadChart();

    // MutationObserver for theme changes
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          console.log('Theme changed, reloading chart...'); // Optional logging
          // Destroy existing chart and reload to apply new theme options
          if (this.chart) {
            this.chart.destroy();
          }
          this.loadChart();
        }
      });
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true, // Observe attribute changes
    });
  }

  ngOnDestroy(): void {
    // Add OnDestroy lifecycle hook
    if (this.themeObserver) {
      this.themeObserver.disconnect(); // Disconnect observer on component destroy
    }
  }

  loadChart() {
    if (!this.myChartCanvas) {
      console.error('Canvas element not found for chart.');
      return;
    }
    const ctx = this.myChartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      console.error('Could not get 2D context from canvas.');
      return;
    }

    // Function to get color based on the original label
    const getColor = (key) => {
      // Extract the original label part if the key includes a count
      const baseLabel = key.split(' (')[0];
      return colors[baseLabel] || 'gray'; // Fallback color
    };

    // Calculate totals and create combined labels
    const totalFlow = (this.allAiResponses || 0) + (this.deflectedAiResponses || 0);
    const totalLabel = `${SANKEY_LABELS.TOTAL_CONVERSATIONS} (${totalFlow})`;
    const nonDeflectedLabel = `${SANKEY_LABELS.NON_DEFLECTED_CONVERSATIONS} (${this.allAiResponses || 0})`;
    const deflectedLabel = `${SANKEY_LABELS.DEFLECTED_CONVERSATIONS} (${this.deflectedAiResponses || 0})`;
    const staleLabel = `${SANKEY_LABELS.STALE_INTAKE} (${this.deflectedAiResponseCounts?.stale || 0})`;
    const closedSubmitterLabel = `${SANKEY_LABELS.CLOSED_BY_SUBMITTER} (${this.deflectedAiResponseCounts?.closedBySubmitter || 0})`;
    const closedInternalLabel = `${SANKEY_LABELS.CLOSED_BY_INTERNAL_USER} (${this.deflectedAiResponseCounts?.closedByInternalUser || 0})`;
    const closedExternalLabel = `${SANKEY_LABELS.CLOSED_BY_EXTERNAL_USER} (${this.deflectedAiResponseCounts?.closedByExternalUser || 0})`;

    // Prepare chart data using combined labels
    const chartData = [
      { from: totalLabel, to: nonDeflectedLabel, flow: this.allAiResponses },
      { from: totalLabel, to: deflectedLabel, flow: this.deflectedAiResponses },
      // Use combined labels for deflected breakdowns
      ...(this.deflectedAiResponseCounts?.stale
        ? [{ from: deflectedLabel, to: staleLabel, flow: this.deflectedAiResponseCounts.stale }]
        : []),
      ...(this.deflectedAiResponseCounts?.closedBySubmitter
        ? [{ from: deflectedLabel, to: closedSubmitterLabel, flow: this.deflectedAiResponseCounts.closedBySubmitter }]
        : []),
      ...(this.deflectedAiResponseCounts?.closedByInternalUser
        ? [{ from: deflectedLabel, to: closedInternalLabel, flow: this.deflectedAiResponseCounts.closedByInternalUser }]
        : []),
      ...(this.deflectedAiResponseCounts?.closedByExternalUser
        ? [{ from: deflectedLabel, to: closedExternalLabel, flow: this.deflectedAiResponseCounts.closedByExternalUser }]
        : []),
    ].filter((item) => item.flow > 0); // Ensure we don't add flows with 0 value if counts are missing

    this.chart = new Chart(ctx, {
      type: 'sankey',
      data: {
        datasets: [
          {
            label: 'deflections_dataset',
            data: chartData, // Use the updated chartData
            colorFrom: (c) => getColor(c.dataset.data[c.dataIndex].from),
            colorTo: (c) => getColor(c.dataset.data[c.dataIndex].to),
            color: this.isDarkMode() ? '#ffffff' : '#000000',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,

        font: {
          size: 16,
        },
        layout: {
          padding: {
            bottom: 20,
          },
        },
      } as any,
    });
  }

  isDarkMode(): boolean {
    return localStorage.getItem('theme') === 'dark';
  }

  renderMarkdown(markdown: string) {
    const md = new Markdown('commonmark', {
      xhtmlOut: true,
      breaks: true,
      html: true,
    });

    const html = md.render(markdown);

    return html;
  }

  getDateRange() {
    const params: any = {
      timezone: this.timezone,
    };

    if (this.dateRange.type === DATE_RANGE_TYPES.RELATIVE) {
      // Unlike with standard reporting, we want this to include the current day
      params.dateRange = {
        ...this.dateRange,
        endIndex: 0,
      };
    } else {
      params.dateRange = {
        type: DATE_RANGE_TYPES.ABSOLUTE,
        startDate: moment(this.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.endDate).format('YYYY-MM-DD'),
      };
    }

    return params;
  }

  async changeDate(isEnd: boolean) {
    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        min: isEnd ? this.startDate : null,
        selected: isEnd ? this.endDate : this.startDate,
        title: isEnd ? 'Select end date' : 'Select start date',
      },
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then((data) => {
      const result = data?.data?.value;

      if (result && result !== 'Invalid date') {
        const newDate = moment(result).toDate();

        if (isEnd) {
          this.endDate = newDate;
        } else {
          this.startDate = newDate;
        }

        this.isDateRangeInvalid = this.startDate.getTime() > this.endDate.getTime();

        if (!this.isDateRangeInvalid) {
          this.getConversationResponses();
        }
      }
    });
  }

  async showDateRangeOptions() {
    this.searchablePopoverService.dateValues({
      event,
      callback: (id: string, _name: string, object: any) => {
        this.dateRange = object;

        this.isDateRangeInvalid =
          this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE && this.startDate.getTime() > this.endDate.getTime();

        if (!this.isDateRangeInvalid) {
          this.getConversationResponses();
        }
      },
    });
  }

  async getConversationResponses() {
    const dateData = this.getDateRange();

    // Add project filtering if a project is selected
    const projectParam = this.authService.selectedProject ? `&projectId=${this.authService.selectedProject.id}` : '';

    return await Promise.all([
      this.apiService
        .getPromise(
          `/reporting/conversation-count-by-status/?dateRange=${JSON.stringify(dateData.dateRange)}&timezone=${dateData.timezone}${projectParam}`,
        )
        .then((res: ConversationCountCall) => {
          res.baseStatuses.closed = res.baseStatuses.closed || 0;
          res.baseStatuses.inProgress = res.baseStatuses.inProgress || 0;
          res.baseStatuses.draft = res.baseStatuses.draft || 0;
          res.baseStatuses.open = res.baseStatuses.open || 0;
          res.baseStatuses.onHold = res.baseStatuses.onHold || 0;

          this.allAiResponses =
            res.baseStatuses.draft +
            res.baseStatuses.closed +
            res.baseStatuses.inProgress +
            res.baseStatuses.open +
            res.baseStatuses.onHold;
          this.deflectedAiResponseCounts = res.deflectedStatuses;
          this.deflectedAiResponses = Object.values(res.deflectedStatuses ?? {}).reduce((acc, curr) => acc + curr, 0);
          this.isInitialized = true;
        })
        .catch((err) => {
          console.log('error: ', err);
        }),
      this.loadResponses(),
    ]);
  }

  async exportAsCsv() {
    // emit analytics events
    this.analyticsService.trackEvent('AI Analytics', 'Export CSV');
    // create date object
    const date = new Date();
    // retrieve and map data to headers
    // initial fetch, then loop over pages using cursor
    const pages = [];
    let cursors;

    do {
      const nextPage = await this.fetchResponses({
        limit: this.limit,
        order: [this.sortField],
        descending: this.sortDesc,
        cursor: cursors?.next,
      });

      pages.push(nextPage);
      cursors = nextPage.cursors;
    } while (cursors.hasNext);
    // flatten and format results
    const results = pages.flatMap((page) => {
      return page.data.map((row) => {
        return {
          query: row.query,
          aiResponse: row.response,
          userFeedback: JSON.stringify(row.feedbacks),
          customer: row.conversation?.customer?.name || null,
          ticketType: row.conversation?.ticketType?.name || null,
          created: row.createdAt,
        };
      });
    });
    // declare headers
    const headers = ['query', 'aiResponse', 'userFeedback', 'customer', 'ticketType', 'created'];
    // export combination as CSV
    downloadCSV(results, headers, `ai-analytics-${date.getTime()}`);
  }

  fetchResponses = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<AiResponse>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
    };

    const where: WhereClause[] = [];

    const { dateRange, timezone } = this.getDateRange();

    options.dateRange = dateRange;
    options.timezone = timezone;

    if (this.filterType === 'no_response') {
      where.push({
        field: 'response',
        operator: '==',
        value: '',
      });
    } else if (this.filterType === 'has_response') {
      where.push({
        field: 'response',
        operator: '!=',
        value: '',
      });
    }

    // Add project filtering if a project is selected
    if (this.authService.selectedProject) {
      where.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    const filterWheres = getWhereClausesFromFilters(this.filters);

    if (filterWheres.length) {
      where.push(...filterWheres);
    }

    if (where.length) {
      options.where = where;
    }

    // TODO create a service
    let result = (await this.apiService.postPromise('/ai-responses/list', options)) as any;

    result.data = result.data.map((aiRes) => {
      return {
        ...aiRes,
        formattedQuery: aiRes.query ? getHtmlFromSlackMd(aiRes.query) : '',
        formattedResponse: aiRes.response ? this.renderMarkdown(aiRes.response) : '',
      };
    });

    return result;
  };

  responsesDateSource = createDataSource(this.fetchResponses);

  async loadResponses() {
    this.isLoading = true;

    await this.responsesDateSource.firstPage();

    this.isLoading = false;
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.loadResponses();
  }
}
